import { useQuery } from "@tanstack/react-query";

import {
  type AnalyticsGranularity,
  type AnalyticsRange,
  type BreakdownType,
  type DesktopProperty,
  fetchAnalyticsOverview,
  fetchBreakdown,
  fetchDesktopBreakdown,
  fetchDesktopOverview,
  fetchEventCounts,
  fetchStatsOverview,
  fetchTopPages,
  fetchTopReferrers,
} from "../api/statsApi";

export const statsKeys = {
  overview: ["stats", "overview"] as const,
};

const refetchIntervalForRange = (range: AnalyticsRange): number => {
  if (range === "live") return 15_000;

  if (range === "1d") return 60_000;

  return 5 * 60_000;
};

export const analyticsKeys = {
  overview: (range: AnalyticsRange, granularity: AnalyticsGranularity) =>
    ["stats", "analytics", "overview", range, granularity] as const,

  topPages: (range: AnalyticsRange, limit: number) =>
    ["stats", "analytics", "top-pages", range, limit] as const,

  topReferrers: (range: AnalyticsRange, limit: number) =>
    ["stats", "analytics", "top-referrers", range, limit] as const,

  breakdown: (range: AnalyticsRange, type: BreakdownType, limit: number) =>
    ["stats", "analytics", "breakdown", type, range, limit] as const,

  events: (range: AnalyticsRange) =>
    ["stats", "analytics", "events", range] as const,

  desktopOverview: (range: AnalyticsRange) =>
    ["stats", "analytics", "desktop", "overview", range] as const,

  desktopBreakdown: (
    range: AnalyticsRange,

    property: DesktopProperty,

    limit: number,
  ) =>
    [
      "stats",

      "analytics",

      "desktop",

      "breakdown",

      property,

      range,

      limit,
    ] as const,
};

export const useStatsOverviewQuery = () =>
  useQuery({
    queryKey: statsKeys.overview,

    queryFn: fetchStatsOverview,

    refetchInterval: 60_000,
  });

export const useAnalyticsOverviewQuery = (
  range: AnalyticsRange,

  granularity: AnalyticsGranularity,
) =>
  useQuery({
    queryKey: analyticsKeys.overview(range, granularity),

    queryFn: () => fetchAnalyticsOverview(range, granularity),

    refetchInterval: refetchIntervalForRange(range),
  });

export const useTopPagesQuery = (range: AnalyticsRange, limit = 10) =>
  useQuery({
    queryKey: analyticsKeys.topPages(range, limit),

    queryFn: () => fetchTopPages(range, limit),

    refetchInterval: refetchIntervalForRange(range),
  });

export const useTopReferrersQuery = (range: AnalyticsRange, limit = 10) =>
  useQuery({
    queryKey: analyticsKeys.topReferrers(range, limit),

    queryFn: () => fetchTopReferrers(range, limit),

    refetchInterval: refetchIntervalForRange(range),
  });

export const useBreakdownQuery = (
  range: AnalyticsRange,

  type: BreakdownType,

  limit = 10,
) =>
  useQuery({
    queryKey: analyticsKeys.breakdown(range, type, limit),

    queryFn: () => fetchBreakdown(range, type, limit),

    refetchInterval: refetchIntervalForRange(range),
  });

export const useEventCountsQuery = (range: AnalyticsRange) =>
  useQuery({
    queryKey: analyticsKeys.events(range),

    queryFn: () => fetchEventCounts(range),

    refetchInterval: refetchIntervalForRange(range),
  });

export const useDesktopOverviewQuery = (range: AnalyticsRange) =>
  useQuery({
    queryKey: analyticsKeys.desktopOverview(range),

    queryFn: () => fetchDesktopOverview(range),

    refetchInterval: refetchIntervalForRange(range),
  });

export const useDesktopBreakdownQuery = (
  range: AnalyticsRange,

  property: DesktopProperty,

  limit = 10,
) =>
  useQuery({
    queryKey: analyticsKeys.desktopBreakdown(range, property, limit),

    queryFn: () => fetchDesktopBreakdown(range, property, limit),

    refetchInterval: refetchIntervalForRange(range),
  });
